import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../cx';

// D-09 §15.9 — Tooltip. 카드 목록처럼 overflow:auto/hidden 조상 안에 있으면 이전의
// absolute+::after 방식은 그 경계에서 위·아래·좌우로 잘렸다(2026-10-07). 그래서 트리거
// 위치를 getBoundingClientRect로 재서 document.body에 포탈 렌더링하는 방식으로 바꿨다 —
// 어떤 overflow 조상에도 잘리지 않고, 화면 가장자리에서는 clamp해서 벗어나지 않는다.
export interface TooltipProps extends React.HTMLAttributes<HTMLSpanElement> {
  tip: string;
}

const GAP = 8;
const MARGIN = 8;

export function Tooltip({ tip, className = '', children, ...props }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<{ top: number; left: number } | null>(null);

  // 두 단계 — 먼저 화면 밖(opacity 0)에 마운트해 크기를 잰 뒤, 트리거 기준 좌표를 계산해
  // 옮긴다. 한 번에 최종 위치로 그리면 폭을 몰라 중앙 정렬·clamp를 할 수 없다.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !bubbleRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const bubble = bubbleRef.current.getBoundingClientRect();

    let top = trigger.top - bubble.height - GAP;
    if (top < MARGIN) top = trigger.bottom + GAP; // 위쪽 공간이 없으면 아래로 전환

    let left = trigger.left + trigger.width / 2 - bubble.width / 2;
    left = Math.min(Math.max(left, MARGIN), window.innerWidth - bubble.width - MARGIN);

    setStyle({ top, left });
  }, [open]);

  function close() {
    setOpen(false);
    setStyle(null);
  }

  useLayoutEffect(() => {
    if (!open) return;
    // 스크롤 중엔 트리거가 커서 아래에서 빠져나가도 hover 상태가 그대로 남을 수 있어
    // 위치가 어긋난 채 떠 있게 된다 — 스크롤 시작하면 닫는다.
    window.addEventListener('scroll', close, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <span className={cx('tip', className)}>
      <span
        ref={triggerRef}
        className="trigger"
        tabIndex={0}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={close}
        onFocus={() => setOpen(true)}
        onBlur={close}
        {...props}
      >
        {children}
      </span>
      {open && createPortal(
        <span
          ref={bubbleRef}
          className="tip-bubble"
          style={style ?? { top: -9999, left: -9999, opacity: 0 }}
        >
          {tip}
        </span>,
        document.body,
      )}
    </span>
  );
}
